import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { toast } from 'sonner';
import { AppLayout } from '@/components/AppLayout';
import { ImportDialog } from '@/components/ImportDialog';
import { ProfileCard } from '@/components/ProfileCard';
import { UserManagementCard } from '@/components/UserManagementCard';
import { useAuthStore } from '@/stores/auth';
import { useTenant } from '@/hooks/useTenant';
import { useSystemInfo } from '@/hooks/useSystemInfo';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Upload,
  Download,
  Database,
  Loader2,
  Check,
  AlertCircle,
  Users,
  Building2,
  UserCircle,
  TrendingUp,
  Cpu,
  AlertTriangle,
} from 'lucide-react';
import { api } from '@/lib/api';

type ExportEntity = 'leads' | 'accounts' | 'contacts' | 'opportunities' | 'backup';
type ExportStatus = 'idle' | 'loading' | 'success' | 'error';

interface ExportState {
  status: ExportStatus;
  error?: string;
}

export function SettingsPage() {
  const user = useAuthStore(state => state.user);
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const { activeTenantId } = useTenant();
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [isDeletingAllData, setIsDeletingAllData] = useState(false);
  const { data: systemInfo, isLoading: systemInfoLoading } = useSystemInfo();
  const [exportStates, setExportStates] = useState<Record<ExportEntity, ExportState>>({
    leads: { status: 'idle' },
    accounts: { status: 'idle' },
    contacts: { status: 'idle' },
    opportunities: { status: 'idle' },
    backup: { status: 'idle' },
  });

  const handleExport = async (entity: ExportEntity) => {
    // Set loading state
    setExportStates(prev => ({
      ...prev,
      [entity]: { status: 'loading' },
    }));

    try {
      switch (entity) {
        case 'leads':
          await api.exportLeads();
          break;
        case 'accounts':
          await api.exportAccounts();
          break;
        case 'contacts':
          await api.exportContacts();
          break;
        case 'opportunities':
          await api.exportOpportunities();
          break;
        case 'backup':
          await api.exportBackup();
          break;
      }

      // Set success state briefly
      setExportStates(prev => ({
        ...prev,
        [entity]: { status: 'success' },
      }));

      // Reset to idle after 2 seconds
      setTimeout(() => {
        setExportStates(prev => ({
          ...prev,
          [entity]: { status: 'idle' },
        }));
      }, 2000);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Export failed';
      setExportStates(prev => ({
        ...prev,
        [entity]: { status: 'error', error: errorMessage },
      }));

      // Reset to idle after 3 seconds
      setTimeout(() => {
        setExportStates(prev => ({
          ...prev,
          [entity]: { status: 'idle' },
        }));
      }, 3000);
    }
  };

  const getExportButtonContent = (entity: ExportEntity) => {
    const state = exportStates[entity];
    switch (state.status) {
      case 'loading':
        return (
          <>
            <Loader2 className="w-3 h-3 animate-spin" />
            <span>Exporting...</span>
          </>
        );
      case 'success':
        return (
          <>
            <Check className="w-3 h-3" />
            <span>Downloaded!</span>
          </>
        );
      case 'error':
        return (
          <>
            <AlertCircle className="w-3 h-3" />
            <span>Failed</span>
          </>
        );
      default:
        return <span>Export CSV</span>;
    }
  };

  const getBackupButtonContent = () => {
    const state = exportStates.backup;
    switch (state.status) {
      case 'loading':
        return (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Downloading...
          </>
        );
      case 'success':
        return (
          <>
            <Check className="w-4 h-4" />
            Backup Complete!
          </>
        );
      case 'error':
        return (
          <>
            <AlertCircle className="w-4 h-4" />
            Backup Failed
          </>
        );
      default:
        return (
          <>
            <Download className="w-4 h-4" />
            Download Full Backup
          </>
        );
    }
  };

  const getExportButtonClass = (entity: ExportEntity) => {
    const state = exportStates[entity];
    const baseClass =
      'px-3 py-1.5 text-xs font-medium rounded transition-colors whitespace-nowrap shrink-0 flex items-center gap-1.5';
    switch (state.status) {
      case 'loading':
        return `${baseClass} text-muted-foreground bg-muted cursor-not-allowed`;
      case 'success':
        return `${baseClass} text-green-600 bg-green-100`;
      case 'error':
        return `${baseClass} text-red-600 bg-red-100`;
      default:
        return `${baseClass} text-primary bg-primary/10 hover:bg-primary/20`;
    }
  };

  const handleDeleteAllData = async () => {
    setIsDeletingAllData(true);

    try {
      await Promise.resolve();
      toast.error('Delete all data is not yet implemented.');
    } finally {
      setIsDeletingAllData(false);
      setDeleteAllDialogOpen(false);
    }
  };

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div>
          <h1 className="text-2xl font-bold text-foreground">Settings</h1>
          <p className="text-muted-foreground mt-1">
            Manage your account and application preferences
          </p>
        </div>

        {/* Settings Grid */}
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Profile Card */}
          <ProfileCard />

          {/* User Management Card - Admin Only (M4.5.2.1, M4.5.2.2) */}
          <UserManagementCard />

          {/* Import Data Card */}
          <div className="bg-card rounded-xl border border-border overflow-hidden">
            <div className="p-4 sm:p-5 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center shrink-0">
                  <Upload className="w-5 h-5 text-green-600" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold text-foreground">Import Data</h2>
                  <p className="text-sm text-muted-foreground">Import records from CSV files</p>
                </div>
              </div>
            </div>
            <div className="p-4 sm:p-5">
              <p className="text-sm text-muted-foreground mb-4">
                Upload a CSV file to import leads, accounts, or contacts. The system will guide you
                through field mapping.
              </p>
              <button
                onClick={() => setImportDialogOpen(true)}
                className="w-full sm:w-auto px-4 py-2.5 sm:py-2 rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 transition-colors flex items-center justify-center sm:inline-flex gap-2"
              >
                <Upload className="w-4 h-4" />
                Start Import
              </button>
            </div>
          </div>

          {/* Export & Backup Card */}
          <div className="bg-card rounded-xl border border-border overflow-hidden">
            <div className="p-4 sm:p-5 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                  <Download className="w-5 h-5 text-amber-600" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold text-foreground">Export & Backup</h2>
                  <p className="text-sm text-muted-foreground">Download your data</p>
                </div>
              </div>
            </div>
            <div className="p-4 sm:p-5">
              <p className="text-sm text-muted-foreground mb-4">
                Export individual entities as CSV or download a complete backup of all your data.
              </p>
              <div className="space-y-3">
                {/* Leads Export */}
                <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-muted/50">
                  <div className="flex items-center gap-3 min-w-0">
                    <Users className="w-5 h-5 text-muted-foreground shrink-0" />
                    <span className="text-sm font-medium text-foreground">Leads</span>
                  </div>
                  <button
                    onClick={() => handleExport('leads')}
                    disabled={exportStates.leads.status === 'loading'}
                    className={getExportButtonClass('leads')}
                  >
                    {getExportButtonContent('leads')}
                  </button>
                </div>

                {/* Accounts Export */}
                <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-muted/50">
                  <div className="flex items-center gap-3 min-w-0">
                    <Building2 className="w-5 h-5 text-muted-foreground shrink-0" />
                    <span className="text-sm font-medium text-foreground">Accounts</span>
                  </div>
                  <button
                    onClick={() => handleExport('accounts')}
                    disabled={exportStates.accounts.status === 'loading'}
                    className={getExportButtonClass('accounts')}
                  >
                    {getExportButtonContent('accounts')}
                  </button>
                </div>

                {/* Contacts Export */}
                <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-muted/50">
                  <div className="flex items-center gap-3 min-w-0">
                    <UserCircle className="w-5 h-5 text-muted-foreground shrink-0" />
                    <span className="text-sm font-medium text-foreground">Contacts</span>
                  </div>
                  <button
                    onClick={() => handleExport('contacts')}
                    disabled={exportStates.contacts.status === 'loading'}
                    className={getExportButtonClass('contacts')}
                  >
                    {getExportButtonContent('contacts')}
                  </button>
                </div>

                {/* Opportunities Export */}
                <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-muted/50">
                  <div className="flex items-center gap-3 min-w-0">
                    <TrendingUp className="w-5 h-5 text-muted-foreground shrink-0" />
                    <span className="text-sm font-medium text-foreground">Opportunities</span>
                  </div>
                  <button
                    onClick={() => handleExport('opportunities')}
                    disabled={exportStates.opportunities.status === 'loading'}
                    className={getExportButtonClass('opportunities')}
                  >
                    {getExportButtonContent('opportunities')}
                  </button>
                </div>
              </div>

              {/* Full Backup */}
              <div className="mt-6 p-3 sm:p-4 rounded-lg border border-primary/20 bg-primary/5">
                <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <Database className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-foreground">Full Data Backup</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Download all your data (Leads, Accounts, Contacts, Opportunities) as a JSON
                      file. Your data, your control.
                    </p>
                    <button
                      onClick={() => handleExport('backup')}
                      disabled={exportStates.backup.status === 'loading'}
                      className={`mt-3 w-full sm:w-auto px-4 py-2.5 sm:py-2 text-sm font-medium rounded-lg transition-colors flex items-center justify-center sm:inline-flex gap-2 ${
                        exportStates.backup.status === 'loading'
                          ? 'text-muted-foreground bg-muted cursor-not-allowed'
                          : exportStates.backup.status === 'success'
                            ? 'text-green-700 bg-green-100'
                            : exportStates.backup.status === 'error'
                              ? 'text-red-700 bg-red-100'
                              : 'text-primary-foreground bg-primary hover:bg-primary/90'
                      }`}
                    >
                      {getBackupButtonContent()}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tenant Admin Quick Links - Admin Only */}
          {isAdmin && (
            <div className="bg-card rounded-xl border border-border overflow-hidden">
              <div className="p-4 sm:p-5 border-b border-border">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <Building2 className="w-5 h-5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-lg font-semibold text-foreground">Tenant Management</h2>
                    <p className="text-sm text-muted-foreground">
                      Quick access to tenant administration pages
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-4 sm:p-5 space-y-2">
                <Link
                  to="/admin/dashboard"
                  className="block px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  Admin Dashboard
                </Link>
                <Link
                  to="/admin/tenants"
                  className="block px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  Tenants
                </Link>
                <Link
                  to="/admin/tenants/users"
                  className="block px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  Tenant Users
                </Link>
                <Link
                  to="/admin/tenants/onboarding"
                  className="block px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  Onboarding
                </Link>
                {activeTenantId && (
                  <Link
                    to="/admin/tenants/onboarding/$tenantId/status"
                    params={{ tenantId: activeTenantId }}
                    className="block px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  >
                    Active Onboarding Status
                  </Link>
                )}
              </div>
            </div>
          )}
        </div>

        {/* System Information Card */}
        <div className="bg-card rounded-xl border border-border overflow-hidden">
          <div className="p-4 sm:p-5 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                <Cpu className="w-5 h-5 text-muted-foreground" />
              </div>
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-foreground">System Information</h2>
                <p className="text-sm text-muted-foreground">Application status and usage</p>
              </div>
            </div>
          </div>
          <div className="p-4 sm:p-5">
            {systemInfoLoading ? (
              <div className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} className="h-20 rounded-lg" />
                  ))}
                </div>
                <Skeleton className="h-10 w-full rounded-lg" />
              </div>
            ) : (
              <>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  {/* Version */}
                  <div className="p-4 rounded-lg bg-muted/50">
                    <p className="text-sm text-muted-foreground">Version</p>
                    <p className="text-lg font-semibold text-foreground mt-1">
                      {systemInfo?.version ?? '1.0.0'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {systemInfo?.phase ?? 'Phase 1'}
                    </p>
                  </div>

                  {/* Total Records */}
                  <div className="p-4 rounded-lg bg-muted/50">
                    <p className="text-sm text-muted-foreground">Total Records</p>
                    <p className="text-lg font-semibold text-foreground mt-1">
                      {systemInfo?.records?.total?.toLocaleString() ?? '—'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Leads, Accounts, Contacts, Opportunities
                    </p>
                  </div>

                  {/* Environment */}
                  <div className="p-4 rounded-lg bg-muted/50">
                    <p className="text-sm text-muted-foreground">Environment</p>
                    <p className="text-lg font-semibold text-foreground mt-1 capitalize">
                      {systemInfo?.environment ?? 'Production'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {systemInfo?.platform ?? 'Cloudflare Workers'}
                    </p>
                  </div>

                  {/* Active Users */}
                  <div className="p-4 rounded-lg bg-muted/50">
                    <p className="text-sm text-muted-foreground">Active Users</p>
                    <p className="text-lg font-semibold text-foreground mt-1">
                      {systemInfo?.users?.active ?? '—'} / 10
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {systemInfo?.users?.total ?? 0} total users
                    </p>
                  </div>
                </div>

                {/* Platform Info */}
                <div className="mt-6 pt-6 border-t border-border">
                  <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1.5">
                      <Database className="w-4 h-4" />
                      Powered by Cloudflare
                    </span>
                    <span>•</span>
                    <span>D1 Database</span>
                    <span>•</span>
                    <span>Workers Runtime</span>
                    <span>•</span>
                    <span>Free Tier</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Danger Zone - Admin Only */}
        {isAdmin && (
          <div className="bg-card rounded-xl border border-destructive/20 overflow-hidden">
            <div className="p-4 sm:p-5 border-b border-destructive/20">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center shrink-0">
                  <AlertTriangle className="w-5 h-5 text-destructive" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold text-foreground">Danger Zone</h2>
                  <p className="text-sm text-muted-foreground">Irreversible actions</p>
                </div>
              </div>
            </div>
            <div className="p-4 sm:p-5">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="min-w-0">
                  <p className="font-medium text-foreground">Delete All Data</p>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Permanently delete all records. This cannot be undone.
                  </p>
                </div>
                <Button
                  variant="outline"
                  className="w-full sm:w-auto text-destructive border-destructive hover:bg-destructive/10 hover:text-destructive"
                  onClick={() => setDeleteAllDialogOpen(true)}
                >
                  Delete All
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete All Data Confirmation Dialog */}
      <Dialog open={deleteAllDialogOpen} onClose={() => setDeleteAllDialogOpen(false)}>
        <DialogContent onClose={() => setDeleteAllDialogOpen(false)} className="max-w-md">
          <DialogHeader>
            <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center mb-3">
              <AlertTriangle className="w-5 h-5 text-destructive" />
            </div>
            <DialogTitle>Delete All Data?</DialogTitle>
            <DialogDescription>This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground px-6 pb-6 -mt-2">
            All leads, accounts, contacts, and opportunities will be permanently deleted. Make sure
            you have a backup before proceeding.
          </p>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteAllDialogOpen(false)}
              disabled={isDeletingAllData}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteAllData}
              disabled={isDeletingAllData}
            >
              {isDeletingAllData ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Everything'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <ImportDialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)} />
    </AppLayout>
  );
}
